import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { addMedicalProfessionalNote } from '~/services/medical-professionals';

interface Props {
  medProfId: number;
  initialNotes: string;
}

// Med pro notes are one running text log (Role.notes), shared with the
// legacy admin; Add Note appends a line to it (HHLTB-2958).
const MedicalProfessionalNotes: React.FC<Props> = ({
  medProfId,
  initialNotes,
}) => {
  const [notes, setNotes] = useState(initialNotes || '');
  const [modalOpen, setModalOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => setNotes(initialNotes || ''), [initialNotes]);

  useEffect(() => {
    if (!modalOpen) return;
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setModalOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  }, [modalOpen]);

  const onPost = async () => {
    if (!draft.trim() || saving) return;
    setSaving(true);
    toast.loading('Saving...');
    try {
      const resp = await addMedicalProfessionalNote(medProfId, draft);
      toast.dismiss();
      if (resp.detail || typeof resp.notes !== 'string') {
        toast.error(resp.detail || 'There was an error saving the note');
        return;
      }
      setNotes(resp.notes);
      setDraft('');
      setModalOpen(false);
      toast.success('Note created successfully');
    } catch (e) {
      toast.dismiss();
      console.error('Error adding med pro note:', e);
      toast.error('There was an error saving the note');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='p-[12px] pt-0'>
      <div className='flex flex-wrap justify-between'>
        <div className='text-[22px]/[28px] font-bold'>Notes</div>
        <button
          onClick={() => setModalOpen(true)}
          className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
        >
          Add Note
        </button>
      </div>

      {notes ? (
        <div className='mt-[12px] max-h-[600px] overflow-y-auto whitespace-pre-wrap break-words bg-[#F4F4F5] p-[12px] text-[14px]/[20px]'>
          {notes}
        </div>
      ) : (
        <div className='mt-[12px] text-[14px]/[20px] text-[#949599]'>
          No notes yet.
        </div>
      )}

      {modalOpen && (
        <div className='fixed left-0 top-0 z-999999 flex h-full min-h-screen w-full items-center justify-center bg-black/90 px-4 py-5'>
          <div className='w-full max-w-142.5 rounded-lg bg-white p-6 dark:bg-boxdark'>
            <h3 className='pb-2 text-xl font-bold sm:text-2xl'>Add Note</h3>
            <label
              htmlFor='med-pro-note'
              className='mt-[16px] block w-full text-[12px]/[14px] font-extrabold'
            >
              Note
            </label>
            <textarea
              rows={6}
              id='med-pro-note'
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className='w-full rounded-[4px] border-[2px] p-[10px] text-[16px]/[20px]'
            />
            <div className='mt-6 flex justify-start space-x-2'>
              <button
                type='button'
                onClick={() => setModalOpen(false)}
                className='rounded-full border-[2px] px-[20px] py-[6px] text-[13px]/[16px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
              >
                Cancel
              </button>
              <button
                type='button'
                onClick={onPost}
                disabled={saving}
                className='rounded-full bg-warning px-[20px] py-[6px] text-[13px]/[16px] font-bold uppercase text-black transition-colors duration-500 hover:bg-yellow disabled:opacity-50'
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MedicalProfessionalNotes;
